import {ChangeDetectionStrategy, Component, QueryList, TemplateRef, ViewChild, ViewChildren} from '@angular/core';
import {ActivatedRoute} from "@angular/router";
import {AsyncPipe} from "@angular/common";
import {AbstractControl, FormControl, FormGroup, FormsModule, ReactiveFormsModule, ValidationErrors, Validators} from "@angular/forms";
import {NgbDateStruct, NgbInputDatepicker, NgbModal, NgbModalRef} from "@ng-bootstrap/ng-bootstrap";
import {Observable} from "rxjs";
import {GlobalService} from "@app/_services/global.service";
import {ApiService} from "@app/_services/api.service";
import {ToastService} from "@app/_services/toast.service";
import {AnnouncementsAdminService} from "@app/_services/admin/announcements-admin.service";
import {SortableDirective, SortEvent} from "@app/_directives/sortable.directive";
import {ModalConfirmComponent} from "@app/_components/modal-confirm/modal-confirm.component";
import {Announcement, SaveAnnouncementRequest} from "@app/_models/api";
import {isControlInvalid} from "@app/_helpers/form-validation";
import {toDateStruct, toIsoDate} from "@app/_helpers/date-struct";
import {TableSearchComponent} from "@app/_components/table-search/table-search.component";
import {TablePagerComponent} from "@app/_components/table-pager/table-pager.component";

const endDateNotBeforeStartDate = (group: AbstractControl): ValidationErrors | null => {
    const start = group.get('startDate');
    const end = group.get('endDate');
    if (!start?.value || !end?.value || start.invalid || end.invalid) {
        return null;
    }
    return toIsoDate(end.value) < toIsoDate(start.value) ? {endBeforeStart: true} : null;
};

@Component({
    selector: 'app-announcements',
    imports: [
        TableSearchComponent,
        TablePagerComponent,
        AsyncPipe,
        FormsModule,
        NgbInputDatepicker,
        SortableDirective,
        ReactiveFormsModule,
        ModalConfirmComponent
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    templateUrl: './announcements.component.html'
})
export class AnnouncementsComponent {
    @ViewChildren(SortableDirective) headers!: QueryList<SortableDirective>;
    @ViewChild('announcementModal') announcementModal!: TemplateRef<any>;
    @ViewChild('confirmationModal') modalConfirmComponent!: ModalConfirmComponent;

    readonly maxMessageLength = 1000;

    form = new FormGroup({
        message: new FormControl<string>("", {nonNullable: true, validators: [Validators.required, Validators.pattern(/\S/), Validators.maxLength(this.maxMessageLength)]}),
        startDate: new FormControl<NgbDateStruct | null>(null, Validators.required),
        endDate: new FormControl<NgbDateStruct | null>(null, Validators.required)
    }, {validators: endDateNotBeforeStartDate});

    savingData = false;
    editingId: string | null = null;
    total$: Observable<number>;
    announcements$: Observable<Announcement[]>;

    private modalRef!: NgbModalRef;

    constructor(
        public globalService: GlobalService,
        public service: AnnouncementsAdminService,
        private route: ActivatedRoute,
        private apiService: ApiService,
        private toastService: ToastService,
        private modalService: NgbModal) {

        this.service.data = this.route.snapshot.data["resolvedData"].data;
        this.announcements$ = this.service.data$;
        this.total$ = this.service.total$;
        this.service.sortColumn = 'startDate';
        this.service.sortDirection = 'desc';
        this.service.refreshDataSource();
    }

    protected readonly isControlInvalid = isControlInvalid;

    get endDateIsInvalid(): boolean {
        const {startDate, endDate} = this.form.controls;
        return isControlInvalid(endDate)
            || (!!this.form.errors?.['endBeforeStart'] && (startDate.dirty || startDate.touched || endDate.dirty || endDate.touched));
    }

    onSort({column, direction}: SortEvent) {
        for (const header of this.headers) {
            if (header.sortable !== column) {
                header.direction = "";
            }
        }

        this.service.sortColumn = column;
        this.service.sortDirection = direction;
    }

    onAdd() {
        this.editingId = null;
        this.form.reset({message: "", startDate: null, endDate: null});
        this.openModal();
    }

    onEdit(announcement: Announcement) {
        this.editingId = announcement.id;
        this.form.reset({
            message: announcement.message,
            startDate: toDateStruct(announcement.startDate),
            endDate: toDateStruct(announcement.endDate)
        });
        this.openModal();
    }

    closeModal() {
        this.modalConfirmComponent.confirmIfDirty(this.form, () => this.modalRef.dismiss());
    }

    onSave() {
        if (this.form.invalid) {
            this.form.markAllAsTouched();
            this.toastService.showDanger("Invalid Data", "Please fill in all fields. The end date cannot be before the start date.");
            return;
        }

        const {message, startDate, endDate} = this.form.getRawValue();
        const request: SaveAnnouncementRequest = {message, startDate: toIsoDate(startDate), endDate: toIsoDate(endDate)};
        const editingId = this.editingId;
        const save$ = editingId
            ? this.apiService.updateAnnouncement(editingId, request)
            : this.apiService.createAnnouncement(request);

        this.savingData = true;
        save$.subscribe({
            next: saved => {
                this.savingData = false;
                this.service.data = editingId
                    ? this.service.data.map(a => a.id === saved.id ? saved : a)
                    : [...this.service.data, saved];
                this.service.refreshDataSource();
                this.toastService.showSuccess(editingId ? 'Announcement Updated' : 'Announcement Created', 'Saved successfully.');
                this.modalRef.close();
            },
            error: err => {
                this.savingData = false;
                this.toastService.showDanger('Error Saving Announcement', err?.error?.detail || 'Unable to save announcement.');
            }
        });
    }

    onDelete(announcement: Announcement) {
        this.modalConfirmComponent.open(`Delete this announcement? "${announcement.message}"`);
        this.modalConfirmComponent.modalRef.result.then(() => {
            this.globalService.showWaitOverlay();
            this.apiService.deleteAnnouncement(announcement.id).subscribe({
                next: () => {
                    this.globalService.hideWaitOverlay();
                    this.service.data = this.service.data.filter(a => a.id !== announcement.id);
                    this.service.refreshDataSource();
                    this.toastService.showSuccess('Announcement Deleted', 'Deleted successfully.');
                },
                error: err => {
                    this.globalService.hideWaitOverlay();
                    this.toastService.showDanger('Error Deleting Announcement', err?.error?.detail || 'Unable to delete announcement.');
                }
            });
        }, () => {
        });
    }

    private openModal() {
        this.modalRef = this.modalService.open(this.announcementModal, {
            modalDialogClass: "modal-dialog-centered modal modal-lg",
            backdrop: "static",
        });
    }
}
